import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  CircularProgress,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Fab,
  Tooltip,
  Zoom,
  Divider,
} from "@mui/material";
import {
  AddCircleOutline as AddCircleOutlineIcon,
  Delete as DeleteIcon,
  AccountTree as AccountTreeIcon,
  ExpandMore as ExpandMoreIcon,
  Add as AddIcon,
  Save as SaveIcon,
  Publish as PublishIcon,
} from "@mui/icons-material";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { fetchForm, updateForm } from "../../../api/services/carePathWay";
import CustomSelect from "../../FormFields/CustomSelect/CustomSelect";
import CustomInput from "../../CustomForm/CustomInput";
import { useSnackbar } from "notistack";
import { QK } from "../../../base/qk";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import SignatureFieldBase64 from "../../CustomForm/SignatureFieldBase64";
import { blobToBase64 } from "../../../utils/blob";

const FormEdit = ({ formId }) => {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const [expanded, setExpanded] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    isActive: false,
  });
  const [managerSignature, setManagerSignature] = useState(null);
  const scrollRef = useRef({});

  const { isLoading, isError, data: remoteData } = useQuery(
    [QK.careplanForm, formId],
    () => fetchForm(formId),
    {
      enabled: !!formId,
      refetchOnWindowFocus: false,
    }
  );

  useEffect(() => {
    if (remoteData) {
      setFormData(remoteData);
    }
  }, [remoteData, formId]);

  useEffect(() => {
    setExpanded(false);
    setConfirmModal({ show: false, isActive: false });
    setManagerSignature(null);
  }, [formId]);

  const mutation = useMutation((updated) => updateForm(formId, updated), {
    onSuccess: () => {
      queryClient.invalidateQueries([QK.careplanForm, formId]);
      enqueueSnackbar(
        confirmModal.isActive
          ? "Template Updated & Published!"
          : "Draft Updated Successfully!",
        { variant: "success" }
      );
      setConfirmModal({ show: false, isActive: false });
      setManagerSignature(null);
    },
    onError: () => {
      enqueueSnackbar("Failed to update template", { variant: "error" });
    },
  });

  const handleAccordionChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
    if (isExpanded) {
      setTimeout(() => {
        scrollRef.current[panel]?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      }, 300);
    }
  };

  const openConfirm = (is_active) => {
    setConfirmModal({ show: true, isActive: is_active });
  };

  const handleConfirmAction = async () => {
    if (!managerSignature) {
      enqueueSnackbar("Manager signature is required to authorize changes.", {
        variant: "error",
      });
      return;
    }

    try {
      const signatureBase64 = await blobToBase64(managerSignature);
      const payload = {
        ...formData,
        is_active: confirmModal.isActive,
        manager_signature: signatureBase64,
      };
      mutation.mutate(payload);
    } catch (error) {
      enqueueSnackbar("Error processing signature", { variant: "error" });
    }
  };

  const addNewSection = () => {
    const newSection = {
      id: Date.now(),
      title: "",
      fields: [
        { label: "", field_type: "singleline", branches: [], options: [] },
      ],
    };
    setFormData({ ...formData, sections: [...formData.sections, newSection] });
  };

  const removeSection = (sIndex) => {
    const updatedSections = formData.sections.filter((_, i) => i !== sIndex);
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleSectionTitleChange = (sIndex, value) => {
    const updatedSections = [...formData.sections];
    updatedSections[sIndex].title = value;
    setFormData({ ...formData, sections: updatedSections });
  };

  const updateNestedField = (fields, path, key, value) => {
    const [idx, ...rest] = path;
    return fields.map((f, i) => {
      if (i !== idx) return f;
      if (rest.length === 0) return { ...f, [key]: value };
      return {
        ...f,
        branches: updateNestedField(f.branches || [], rest, key, value),
      };
    });
  };

  const addBranchToField = (fields, path) => {
    const [idx, ...rest] = path;
    return fields.map((f, i) => {
      if (i !== idx) return f;
      if (rest.length === 0) {
        return {
          ...f,
          field_type: "radio",
          branches: [
            ...(f.branches || []),
            {
              label: "",
              field_type: "singleline",
              order: 1,
              branches: [],
            },
          ],
        };
      }
      return { ...f, branches: addBranchToField(f.branches || [], rest) };
    });
  };

  const removeNestedField = (fields, path) => {
    const [idx, ...rest] = path;
    if (rest.length === 0) return fields.filter((_, i) => i !== idx);
    return fields.map((f, i) => {
      if (i !== idx) return f;
      return { ...f, branches: removeNestedField(f.branches || [], rest) };
    });
  };

  const handleFieldChange = (sIndex, path, key, value) => {
    const updatedSections = [...formData.sections];
    updatedSections[sIndex].fields = updateNestedField(
      updatedSections[sIndex].fields,
      path,
      key,
      value
    );
    setFormData({ ...formData, sections: updatedSections });
  };

  const handleOptionChange = (sIndex, path, optIdx, value) => {
    const updatedSections = [...formData.sections];
    const updateOptions = (fields, p) => {
      const [idx, ...rest] = p;
      return fields.map((f, i) => {
        if (i !== idx) return f;
        if (rest.length === 0) {
          const newOpts = [...(f.options || [])];
          newOpts[optIdx] = { ...newOpts[optIdx], value: value };
          return { ...f, options: newOpts };
        }
        return { ...f, branches: updateOptions(f.branches || [], rest) };
      });
    };
    updatedSections[sIndex].fields = updateOptions(
      updatedSections[sIndex].fields,
      path
    );
    setFormData({ ...formData, sections: updatedSections });
  };

  const addOptionToField = (sIndex, path) => {
    const updatedSections = [...formData.sections];
    const addOpt = (fields, p) => {
      const [idx, ...rest] = p;
      return fields.map((f, i) => {
        if (i !== idx) return f;
        if (rest.length === 0)
          return {
            ...f,
            options: [
              ...(f.options || []),
              { value: "", order: (f.options?.length || 0) + 1 },
            ],
          };
        return { ...f, branches: addOpt(f.branches || [], rest) };
      });
    };
    updatedSections[sIndex].fields = addOpt(
      updatedSections[sIndex].fields,
      path
    );
    setFormData({ ...formData, sections: updatedSections });
  };

  const removeOptionFromField = (sIndex, path, optIdx) => {
    const updatedSections = [...formData.sections];
    const remOpt = (fields, p) => {
      const [idx, ...rest] = p;
      return fields.map((f, i) => {
        if (i !== idx) return f;
        if (rest.length === 0)
          return {
            ...f,
            options: f.options.filter((_, oi) => oi !== optIdx),
          };
        return { ...f, branches: remOpt(f.branches || [], rest) };
      });
    };
    updatedSections[sIndex].fields = remOpt(
      updatedSections[sIndex].fields,
      path
    );
    setFormData({ ...formData, sections: updatedSections });
  };

  const renderFieldEditor = (field, path, sIndex, level = 0) => {
    const hasBranches = field.branches && field.branches.length > 0;
    const panelId = `panel-${sIndex}-${path[0]}`;

    const content = (
      <Box sx={{ mb: level > 0 ? 2 : 0, ml: level * 2 }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1.5,
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              flex: hasBranches
                ? "0 0 calc(66.66% - 8px)"
                : "0 0 calc(83.33% - 8px)",
              minWidth: "200px",
            }}
          >
            <CustomInput
              label={level > 0 ? "Follow-up Question" : "Question Label"}
              placeholder="Enter question here..."
              value={field.label}
              onChange={(e) =>
                handleFieldChange(sIndex, path, "label", e.target.value)
              }
            />
          </Box>
          <Box sx={{ flex: "0 0 auto", display: "flex", gap: 0.5 }}>
            {level === 0 && (
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  const us = [...formData.sections];
                  us[sIndex].fields = addBranchToField(
                    us[sIndex].fields,
                    path
                  );
                  setFormData({ ...formData, sections: us });
                }}
                sx={{
                  color: "#2469F0",
                  border: "1px solid #EAECF0",
                  borderRadius: "6px",
                  p: "6px",
                  "&:hover": { bgcolor: "rgba(36, 105, 240, 0.04)" },
                }}
              >
                <AccountTreeIcon sx={{ fontSize: 16 }} />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                const us = [...formData.sections];
                us[sIndex].fields = removeNestedField(
                  us[sIndex].fields,
                  path
                );
                setFormData({ ...formData, sections: us });
              }}
              sx={{
                color: "#DC2626",
                border: "1px solid #FEE2E2",
                borderRadius: "6px",
                p: "6px",
                "&:hover": { bgcolor: "rgba(220, 38, 38, 0.04)" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Box>

          {hasBranches && (
            <Box sx={{ flex: "0 0 100%" }}>
              <Stack
                direction="row"
                spacing={1.5}
                sx={{ p: 1.5, bgcolor: "#F8FAFC", borderRadius: "8px" }}
              >
                <CustomInput
                  label="Trigger Answer"
                  placeholder="e.g. Yes"
                  value={field.main_answer_label || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      sIndex,
                      path,
                      "main_answer_label",
                      e.target.value
                    )
                  }
                  sx={{ flex: 1 }}
                />
                <CustomInput
                  label="Condition"
                  placeholder="e.g. equals"
                  value={field.branch_condition || ""}
                  onChange={(e) =>
                    handleFieldChange(
                      sIndex,
                      path,
                      "branch_condition",
                      e.target.value
                    )
                  }
                  sx={{ flex: 1 }}
                />
              </Stack>
            </Box>
          )}

          {!hasBranches && (
            <>
              <Box sx={{ flex: "0 0 calc(50% - 8px)", minWidth: "150px" }}>
                <CustomSelect
                  options={[
                    { label: "Single Line Text", value: "singleline" },
                    { label: "Multi Line Text", value: "multiline" },
                    { label: "Rich Text", value: "richtext" },
                    { label: "Select (Dropdown)", value: "select" },
                    { label: "Multi-Select Dropdown", value: "multiselect" },
                    { label: "Number", value: "number" },
                    { label: "Date", value: "date" },
                    { label: "Checkbox", value: "checkbox" },
                    { label: "Signature", value: "signature" },
                  ]}
                  value={field.field_type}
                  onChange={(e) =>
                    handleFieldChange(sIndex, path, "field_type", e.target.value)
                  }
                  placeholder="Select Field Type"
                  fullWidth
                  size="small"
                />
              </Box>

              {(field.field_type === "select" ||
                field.field_type === "multiselect") && (
                <Box sx={{ flex: "0 0 100%" }}>
                  <Box
                    sx={{
                      mt: 1,
                      p: 2,
                      bgcolor: "#FAFBFC",
                      borderRadius: "8px",
                      border: "1px solid #EAECF0",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "0.7rem",
                        fontWeight: 600,
                        color: "#667085",
                        mb: 1.5,
                        display: "block",
                        textTransform: "uppercase",
                        letterSpacing: 0.3,
                      }}
                    >
                      {field.field_type === "multiselect"
                        ? "Multi-Select Options"
                        : "Dropdown Options"}
                    </Typography>
                    <Stack spacing={1}>
                      {field.options?.map((opt, optIdx) => (
                        <Stack key={optIdx} direction="row" spacing={1}>
                          <TextField
                            fullWidth
                            size="small"
                            placeholder={`Option ${optIdx + 1}`}
                            value={opt.value}
                            onChange={(e) =>
                              handleOptionChange(
                                sIndex,
                                path,
                                optIdx,
                                e.target.value
                              )
                            }
                            sx={{
                              "& .MuiInputBase-input": {
                                fontSize: "0.8rem",
                              },
                            }}
                          />
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() =>
                              removeOptionFromField(sIndex, path, optIdx)
                            }
                          >
                            <DeleteIcon sx={{ fontSize: 16 }} />
                          </IconButton>
                        </Stack>
                      ))}
                      <Button
                        size="small"
                        startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                        onClick={() => addOptionToField(sIndex, path)}
                        sx={{
                          alignSelf: "flex-start",
                          fontSize: "0.75rem",
                          color: "#2469F0",
                          textTransform: "none",
                          fontWeight: 600,
                          "&:hover": {
                            bgcolor: "rgba(36, 105, 240, 0.04)",
                          },
                        }}
                      >
                        Add Option
                      </Button>
                    </Stack>
                  </Box>
                </Box>
              )}
            </>
          )}
          {field.branches?.map((branch, bIdx) => (
            <Box sx={{ flex: "0 0 100%" }} key={bIdx}>
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  borderLeft: "3px solid #2469F0",
                  bgcolor: "#F8FAFC",
                  borderRadius: "0 4px 4px 0",
                }}
              >
                {renderFieldEditor(branch, [...path, bIdx], sIndex, level + 1)}
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    );

    if (level === 0) {
      return (
        <Box key={path.join("-")} ref={(el) => (scrollRef.current[panelId] = el)}>
          <Accordion
            expanded={expanded === panelId}
            onChange={handleAccordionChange(panelId)}
            sx={{
              mb: 1.5,
              borderRadius: "8px !important",
              boxShadow: "none",
              "&:before": { display: "none" },
              overflow: "hidden",
              border: "1px solid #EAECF0",
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon sx={{ fontSize: 20 }} />}
              sx={{
                bgcolor: "#F8FAFC",
                minHeight: "40px",
                "& .MuiAccordionSummary-content": {
                  margin: "6px 0",
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: 600,
                  color: "#344054",
                  fontSize: "0.8rem",
                }}
              >
                {field.label || `Question ${path[0] + 1}`}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ p: 2, pt: 2.5, bgcolor: "#FFFFFF" }}>
              {content}
            </AccordionDetails>
          </Accordion>
        </Box>
      );
    }
    return content;
  };

  if (isLoading && !formData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (isError) {
    return (
      <Typography color="error" sx={{ p: 4, textAlign: "center" }}>
        Error loading care plan template.
      </Typography>
    );
  }

  if (!formData) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", p: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ position: "relative", pb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Typography
          sx={{
            fontSize: "0.95rem",
            fontWeight: 600,
            color: "#101828",
            letterSpacing: "-0.01em",
          }}
        >
          Edit Care Plan Template
        </Typography>
        <Typography sx={{ fontSize: "0.7rem", color: "#667085", mt: 0.2 }}>
          Manage sections and nested branching logic
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <CustomInput
          label="Form Title"
          placeholder="Enter care plan title..."
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
        />
      </Box>

      {formData.sections.map((section, sIndex) => (
        <Box key={sIndex} sx={{ mb: 3 }}>
          <Stack direction="row" spacing={1.5} alignItems="center" mb={2}>
            <TextField
              placeholder="Enter section name..."
              variant="standard"
              fullWidth
              value={section.title}
              onChange={(e) => handleSectionTitleChange(sIndex, e.target.value)}
              sx={{
                "& input": {
                  fontWeight: 600,
                  fontSize: "1rem",
                  color: "#101828",
                },
                "& input::placeholder": {
                  fontSize: "0.8rem",
                  color: "#98A2B3",
                },
              }}
            />
            <IconButton
              size="small"
              onClick={() => removeSection(sIndex)}
              sx={{
                color: "#DC2626",
                border: "1px solid #FEE2E2",
                borderRadius: "6px",
                p: "6px",
                "&:hover": { bgcolor: "rgba(220, 38, 38, 0.04)" },
              }}
            >
              <DeleteIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>

          {section.fields.map((field, fIndex) =>
            renderFieldEditor(field, [fIndex], sIndex)
          )}

          <Button
            startIcon={<AddCircleOutlineIcon sx={{ fontSize: 16 }} />}
            fullWidth
            variant="text"
            onClick={() => {
              const ns = [...formData.sections];
              ns[sIndex].fields.push({
                label: "",
                field_type: "singleline",
                branches: [],
                options: [],
              });
              setFormData({ ...formData, sections: ns });
            }}
            sx={{
              mt: 1,
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.8rem",
              color: "#2469F0",
              justifyContent: "flex-start",
              "&:hover": {
                bgcolor: "rgba(36, 105, 240, 0.04)",
              },
            }}
          >
            + Add Question
          </Button>
        </Box>
      ))}

      <Button
        variant="text"
        fullWidth
        onClick={addNewSection}
        sx={{
          py: 2,
          textTransform: "none",
          fontWeight: 600,
          fontSize: "0.85rem",
          color: "#2469F0",
          border: "2px dashed #EAECF0",
          borderRadius: "8px",
          "&:hover": {
            borderColor: "#2469F0",
            bgcolor: "rgba(36, 105, 240, 0.02)",
          },
        }}
      >
        + Add New Section
      </Button>

      <ConfirmModal
        show={confirmModal.show}
        handleClose={() => {
          setConfirmModal({ show: false, isActive: false });
          setManagerSignature(null);
        }}
        handleConfirm={handleConfirmAction}
        isLoading={mutation.isLoading}
        title={confirmModal.isActive ? "Update & Publish" : "Update Draft"}
        body={
          <Box>
            <Typography
              sx={{ mb: 2, fontSize: "0.85rem", color: "#344054" }}
            >
              {confirmModal.isActive
                ? "Please sign below to authorize and publish these updates."
                : "A manager's signature is required to save these changes as a draft."}
            </Typography>
            <Divider sx={{ mb: 2, borderColor: "#EAECF0" }} />
            <Box
              sx={{
                p: 2,
                bgcolor: "#F8FAFC",
                borderRadius: "8px",
                border: "1px solid #EAECF0",
              }}
            >
              <SignatureFieldBase64
                value={managerSignature}
                onChange={(val) => setManagerSignature(val)}
                label="Manager Authorization Signature"
              />
            </Box>
          </Box>
        }
        confirmText={confirmModal.isActive ? "Update Now" : "Save Changes"}
      />

      <Box
        sx={{
          position: "fixed",
          bottom: 32,
          right: 32,
          display: "flex",
          flexDirection: "column",
          gap: 2,
          zIndex: 1000,
        }}
      >
        <Zoom in={true} style={{ transitionDelay: "100ms" }}>
          <Tooltip title="Update Draft" placement="left" arrow>
            <Fab
              onClick={() => openConfirm(false)}
              disabled={mutation.isLoading}
              sx={{
                bgcolor: "rgba(255, 255, 255, 0.9)",
                backdropFilter: "blur(10px)",
                color: "#667085",
                boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                width: 48,
                height: 48,
                "&:hover": { bgcolor: "#FFFFFF" },
              }}
            >
              <SaveIcon sx={{ fontSize: 18 }} />
            </Fab>
          </Tooltip>
        </Zoom>

        <Zoom in={true} style={{ transitionDelay: "200ms" }}>
          <Tooltip title="Update & Publish" placement="left" arrow>
            <Fab
              variant="extended"
              onClick={() => openConfirm(true)}
              disabled={mutation.isLoading}
              sx={{
                px: 2.5,
                bgcolor: "#2469F0",
                color: "#FFFFFF",
                boxShadow: "0 10px 20px rgba(36, 105, 240, 0.3)",
                fontWeight: 600,
                fontSize: "0.75rem",
                height: 48,
                textTransform: "none",
                "&:hover": {
                  bgcolor: "#1A5BD4",
                  boxShadow: "0 15px 25px rgba(36, 105, 240, 0.4)",
                },
              }}
            >
              <PublishIcon sx={{ mr: 1, fontSize: 18 }} />
              {mutation.isLoading ? "Saving..." : "Update & Publish"}
            </Fab>
          </Tooltip>
        </Zoom>
      </Box>
    </Box>
  );
};

export default FormEdit;